import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { Context } from "../../models/context";
import { PostUpload } from "../../services/upload";
import { GetProjects } from "../../services/projects";
import { Project } from "../../models/project";
import "./upload.css";
import { GetBatchNameTaken } from "../../services/hints";
import { useAlerts } from "../../contexts/AlertContext";

const EMAIL_WARNING_MESSAGE = "Warning: No email attached. You will not be able to see the results of the upload without one." +
    "Hit submit again if you are sure you do not want to attach one";

export default function Upload() {
    const context = useContext(Context);
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [sawEmailWarning, setSawEmailWarning] = useState<boolean>(false);
    const [powerlinkerAllowed, setPowerlinkerAllowed] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadStatus, setUploadStatus] = useState<"success" | "error" | null>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const { addError, addAlert } = useAlerts();

    useEffect(() => {
        if (!context.user) {
            navigate("/login?entryPoint=upload");
            return;
        }

        try {
            (new GetProjects()).request(context.user).then(r => setProjects(r));
        }
        catch (error) {
            addError(error as Error, "Error getting projects");
        }
    }, [context.user, navigate]);


    const resetForm = () => {
        formRef.current?.reset();
        setPowerlinkerAllowed(false);
        setSawEmailWarning(false);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setUploadStatus(null);

        const formData = new FormData(e.currentTarget);
        const finalForm = new FormData();
        const batchName = formData.get("batch-name") as string;

        if (batchName) {
            try {
                const batchNameTaken = await (new GetBatchNameTaken(batchName).request(context.user!));

                if (batchNameTaken.isDup) {
                    const msg = `Error uploading batch: Name ${batchName} is already taken. Suggested alternative: ${batchName + Date.now()}`
                    addAlert(msg, 0, "error", true);
                    return;
                }
                finalForm.append("batchName", batchName);
            }
            catch (error) {
                addError(error as Error, "Error uploading batch");
            }
        }
        else {
            finalForm.append("batchName", "HintUpload" + Date.now());
        }


        finalForm.append("file", formData.get("file-upload") as File);
        finalForm.append("projectName", formData.get("project") as string);

        if (formData.get("email") && formData.get("email") !== "") {
            finalForm.append("email", formData.get("email") as string);
        }
        else if (!sawEmailWarning) {
            alert(EMAIL_WARNING_MESSAGE);
            setSawEmailWarning(true);
            return;
        }
        finalForm.append("powerlinkerAllowed", String(powerlinkerAllowed));

        setIsUploading(true);
        try {
            const result = await (new PostUpload().request(finalForm, context.user!));
            if (result?.message) {
                addAlert(result.message);
            }
            setUploadStatus("success");
            resetForm();
        }
        catch (error) {
            addError(error as Error, "Error uploading batch");
            setUploadStatus("error");
        }
        finally {
            setIsUploading(false);
        }

    };

    return (
        <div id="upload-page">

            <div id="upload-wrapper" className="container">
                <h1>Upload Data</h1>
                {projects.length > 0 ? <form id="upload-form" ref={formRef} onSubmit={handleSubmit} encType="multipart/form-data">
                    <ul id="upload-form-body">
                        <li>
                            <label htmlFor="file-upload" id="file-upload-label">Choose a .csv file to upload: </label>
                            <input type="file" id="file-upload" className="custom-file-input" name="file-upload" accept=".csv" />
                        </li>
                        <li>
                            <label htmlFor="batch-name" id="batch-name-label">Batch Name: </label>
                            <input type="text" id="batch-name" name="batch-name" placeholder="Batch" defaultValue="" />
                        </li>
                        <li>
                            <label htmlFor="project-select" id="project-select-label">Project: </label>
                            <select id="project-select" name="project">
                                {projects.map((p, i) => <option key={i} value={p.name}>{p.name}</option>)}
                            </select>
                        </li>
                        <li>
                            <label htmlFor="email" id="email-label">Email to receive results (Optional): </label>
                            <input type="text" id="email" name="email" placeholder="Email" defaultValue="" />
                        </li>
                        <li>
                            <label className="toggle-row" id="upload-toggle">
                                <span className="option-item">Powerlinker Allowed</span>
                                <span className="switch">
                                    <input
                                        type="checkbox"
                                        checked={powerlinkerAllowed}
                                        onChange={(event) => setPowerlinkerAllowed(event.target.checked)}
                                    />
                                    <span className="slider" />
                                </span>
                            </label>
                        </li>
                    </ul>
                    <section className="actions">
                        <button
                            type="submit"
                            id="submit-button"
                            className="section-button"
                            disabled={isUploading}
                        >
                            {isUploading
                                ? <><span className="upload-spinner" aria-hidden="true" /> Uploading…</>
                                : "Submit"
                            }
                        </button>
                        {uploadStatus === "success" && (
                            <p className="upload-status upload-status--success" role="status">
                                ✓ Upload successful!
                            </p>
                        )}
                        {uploadStatus === "error" && (
                            <p className="upload-status upload-status--error" role="alert">
                                ✗ Upload failed. Please try again.
                            </p>
                        )}
                    </section>

                </form>
                    : <p id="loading-message">Loading projects...</p>}
            </div>
        </div>);
}
